import { ZButton } from '@/shared/components/button';
import { ZInput } from '@/shared/components/input';
import { ZTooltip } from '@/shared/components/ZTooltip/ZTooltip';
import { Check, X } from 'lucide-react';
import { useFrameworkWizardStore } from './states';

interface HeaderSectionProps {
  currentStep: number;
  totalSteps: number;
  onStepClick: (step: number) => void;
  onProceed: () => void;
  onClose?: () => void;
  isLoading?: boolean;
  onSaveDraft?: () => void;
  isSavingDraft?: boolean;
  isProceedBlocked?: boolean;
}

export default function HeaderSection({
  currentStep,
  totalSteps,
  onStepClick,
  onProceed,
  onClose,
  isLoading,
  isProceedBlocked,
}: HeaderSectionProps) {
  const {
    frameworkName,
    setFrameworkName,
    completedSteps,
    validationErrors,
    stepTwoData,
    stepFourData,
    setValidationErrors
  } = useFrameworkWizardStore();

  // Add step labels
  const stepLabels = [
    'Define Framework Schema',
    'Import Controls',
    'Refine Controls & Procedures',
    'Configure Response',
  ];

  // Check if proceed button should be disabled
  const isProceedDisabled = () => {
    if (isLoading) return true;
    if (typeof isProceedBlocked === 'boolean' && isProceedBlocked) return true;
    // Step 2 specific validation: require filled template URL
    if (currentStep === 2 && !stepTwoData.filledTemplateUrl) {
      return true;
    }
    // Step 4 specific validation: check for incomplete field selections
    if (currentStep === 4) {
      const hasIncompleteFields = stepFourData.responseFields?.some((field) => {
        return field.isEditable && 
               !field.isFromStepThree && 
               (field.ui_key === 'Select Response Type' || 
                field.key === 'Select Response Type' ||
                field.ui_key?.startsWith('New Field ') ||
                field.key?.startsWith('New Field '));
      });
      
      if (hasIncompleteFields) return true;
    }
    return false;
  };

  return (
    <div className="flex flex-col items-start p-4 gap-4 w-full bg-[#FEFEFE] border-b border-[#E4E4E7]">
      <div className="flex flex-row items-center justify-between w-full">
        {/* Left Section - Close button and framework name input */}
        <div className="flex flex-row items-center gap-3">
          <ZTooltip
            showArrow
            placement="right"
            content="Discard to Return to Agent Home"
            isDisabled={false}
          >
            <button
              className="text-[#18181B] hover:text-gray-600"
              onClick={onClose}
            >
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </ZTooltip>
          <div className="min-w-[300px]">
            <ZInput
              isDisabled={currentStep > 1}
              placeholder="Add Framework Name"
              value={frameworkName}
              maxLength={50}
              onFocus={() => {
                setValidationErrors({
                  frameworkName: '',
                  frameworkExist: '',
                });
              }}
              onChange={(e) => {
                setFrameworkName(e.target.value);
              }}
              style={{color: validationErrors.frameworkExist ? '#F31260' : '#18181B'}}
              classNames={{
                input:
                  `text-sm font-medium placeholder:text-[#A1A1AA] pl-3 `,
                inputWrapper: 'border-none shadow-none bg-transparent px-0',
              }}
              isInvalid={!!validationErrors.frameworkName}
            />
          </div>
        </div>

        {/* Center Section - Step indicators */}
        <div className="flex flex-row justify-center items-start gap-6">
          {Array.from({ length: totalSteps }, (_, index) => {
            const stepNumber = index + 1;
            const isActive = stepNumber === currentStep;
            const isCompleted =
              completedSteps.includes(stepNumber) && index < currentStep;

            return (
              <div
                key={stepNumber}
                className="flex flex-row items-center gap-3"
              >
                {/* Step circle with label */}
                <div className="flex flex-col items-center gap-2">
                  <ZTooltip
                    isDisabled={false}
                    content={stepLabels[index]}
                    placement="bottom"
                    showArrow
                  >
                    <button
                      onClick={() => onStepClick(stepNumber)}
                      disabled={stepNumber > currentStep}
                      className={`flex flex-col justify-center items-center p-1 gap-1 w-6 h-6 rounded-full cursor-pointer hover:opacity-80 transition-opacity disabled:cursor-not-allowed disabled:opacity-50 ${isActive || isCompleted
                          ? 'bg-[#006FEE]'
                          : 'bg-[#F4F4F5]'
                        }`}
                    >
                      {isCompleted ? (
                        <Check className="w-4 h-4 text-white" strokeWidth={2} />
                      ) : (
                        <span
                          className={`text-xs font-medium leading-4 text-center ${isActive ? 'text-white' : 'text-[#71717A]'
                            }`}
                        >
                          {stepNumber}
                        </span>
                      )}
                    </button>
                  </ZTooltip>
                </div>

                {/* Step label - only show when active */}
                {isActive && (
                  <span className="text-xs font-semibold leading-4 text-[#18181B] whitespace-nowrap ml-2">
                    {stepLabels[index]}
                  </span>
                )}

                {/* Connecting line (except for last step) */}
                {index < totalSteps - 1 && (
                  <div className="flex flex-col items-start py-3 gap-1 w-9">
                    <div
                      className={`w-9 h-0 border-t ${isCompleted ? 'border-[#006FEE]' : 'border-[#E4E4E7]'
                        }`}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Right Section - Proceed button */}
        <div className="flex flex-row justify-end items-center">
          <ZButton
            onClick={onProceed}
            disabled={isProceedDisabled()}
            isLoading={isLoading}
            className={`flex flex-row items-center px-3 py-1 h-7 gap-1.5 bg-[#006FEE] rounded-md text-white text-sm font-medium leading-5${isProceedDisabled() ? ' opacity-50 cursor-not-allowed' : ' hover:bg-[#005BB5]'} `}
            style={
              isProceedDisabled()
                ? { pointerEvents: 'none', background: '#006FEE' }
                : {}
            }
          >
            {currentStep === totalSteps ? 'Publish Agent' : 'Proceed'}
          </ZButton>
        </div>
      </div>
    </div>
  );
}
